import React, { useMemo, useState } from 'react';
import CasoCard from './CasoCard';
import { SIN_FILTRO, filtrarCasos, hayFiltro, resumenMotivos, type FiltroCasos, type FiltroMotivo } from './motivos';
import type { Caso } from './tipos';
import { inputChicoCls } from './ui';

/** Un campo del panel de filtros: etiqueta chica arriba del control. */
export const Campo: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="flex flex-col gap-1 text-xs text-gray-500">
    {label}
    {children}
  </label>
);

/** Orden fijo de los chips-toggle del filtro por motivo. */
const OPCIONES_MOTIVO: Array<{ id: FiltroMotivo; label: string }> = [
  { id: 'todos', label: 'Todos' },
  { id: 'fallo_generacion', label: 'Fallo' },
  { id: 'ambiguo', label: 'Ambiguo' },
  { id: 'cambio_creativo', label: 'Creativo' },
  { id: 'sin_clasificar', label: 'Sin clasificar' },
];

/**
 * Sección de tarjetas (Editadas o Control) con checkbox por caso y todo/ninguno. Con
 * `filtros`, arriba de la grilla van los chips por motivo (clasificado por IA) y la
 * búsqueda por texto; todo/ninguno pasan a actuar sobre lo VISIBLE, sin tocar lo que el
 * filtro esconde. El filtro es estado local: no se persiste.
 */
const SeccionCasos: React.FC<{
  titulo: string;
  casos: Caso[];
  sel: Set<string>;
  onSel: (s: Set<string>) => void;
  /** Filtro por motivo + búsqueda (solo Editadas: el control no tiene ediciones ni motivo). */
  filtros?: boolean;
}> = ({ titulo, casos, sel, onSel, filtros = false }) => {
  const [filtro, setFiltro] = useState<FiltroCasos>(SIN_FILTRO);
  const activo = filtros && hayFiltro(filtro);
  const visibles = useMemo(() => (filtros ? filtrarCasos(casos, filtro) : casos), [filtros, casos, filtro]);
  const conteo = useMemo((): Record<FiltroMotivo, number> => {
    const r = resumenMotivos(casos);
    return { todos: casos.length, fallo_generacion: r.fallo, ambiguo: r.ambiguo, cambio_creativo: r.creativo, sin_clasificar: r.sin_clasificar };
  }, [casos]);
  const selVisibles = visibles.filter((c) => sel.has(c.imagen_id)).length;

  const toggle = (id: string) => {
    const s = new Set(sel);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    onSel(s);
  };
  // Sin filtro, "visibles" son todos: sumar todos / sacar todos es lo de siempre.
  const seleccionarVisibles = () => onSel(new Set([...sel, ...visibles.map((c) => c.imagen_id)]));
  const quitarVisibles = () => {
    const s = new Set(sel);
    for (const c of visibles) s.delete(c.imagen_id);
    onSel(s);
  };

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-semibold text-brand-black">{titulo}</h3>
        <span className="text-xs text-gray-500">
          {activo ? `${selVisibles} / ${visibles.length} visibles seleccionados (de ${casos.length})` : `${sel.size} / ${casos.length} seleccionados`}
        </span>
        <button type="button" onClick={seleccionarVisibles} className="text-xs text-blue-600 underline">
          {activo ? 'Seleccionar visibles' : 'Seleccionar todo'}
        </button>
        <button type="button" onClick={quitarVisibles} className="text-xs text-blue-600 underline">
          {activo ? 'Quitar visibles' : 'Ninguno'}
        </button>
      </div>
      {filtros && (
        <div className="flex flex-wrap items-center gap-2">
          {OPCIONES_MOTIVO.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setFiltro((f) => ({ ...f, motivo: o.id }))}
              className={`rounded-full border py-1 px-2.5 text-xs ${
                filtro.motivo === o.id ? 'border-brand-black bg-brand-black text-white' : 'border-gray-300 bg-white text-brand-black hover:border-gray-500'
              }`}
            >
              {o.label} {conteo[o.id]}
            </button>
          ))}
          <input
            value={filtro.texto}
            onChange={(e) => setFiltro((f) => ({ ...f, texto: e.target.value }))}
            placeholder="Buscar en instrucciones, producto, empresa o razón"
            className={`${inputChicoCls} w-80 max-w-full`}
          />
          {activo && (
            <button type="button" onClick={() => setFiltro(SIN_FILTRO)} className="text-xs text-blue-600 underline">
              Limpiar filtro
            </button>
          )}
        </div>
      )}
      {casos.length === 0 && <p className="text-sm text-gray-500">No hay casos en esta sección.</p>}
      {casos.length > 0 && visibles.length === 0 && <p className="text-sm text-gray-500">Ningún caso coincide con el filtro.</p>}
      <div className="grid gap-3 lg:grid-cols-2">
        {visibles.map((c) => (
          <CasoCard key={c.imagen_id} caso={c} className={sel.has(c.imagen_id) ? '' : 'opacity-60'}>
            <label className="mt-auto flex items-center gap-2 pt-1 text-xs text-gray-600">
              <input type="checkbox" checked={sel.has(c.imagen_id)} onChange={() => toggle(c.imagen_id)} className="h-4 w-4 accent-brand-black" />
              Incluir en la corrida
            </label>
          </CasoCard>
        ))}
      </div>
    </section>
  );
};

export default SeccionCasos;
